// screens/Clark/answer/railPayload.js — RUN PN.
//
// MOVED, NOT REWRITTEN, from screens/Clark/index.jsx:1120-1180 (pre-extraction numbering) — the
// 60-line mapping that sat inline inside the `<SourcesColumn turns={…}>` prop. The statements below
// are the byte-identical strings that were removed, de-indented by 10 columns — they sat at the 12
// of JSX nesting and now sit at the 2 of a function body — and nothing else. Call site: `answerTurns.map((t) => railPayload(t))`, which
// keeps the exact call shape rather than relying on this function's arity.
//
// One answered turn in, one rail entry out. `SourcesColumn` renders the result.
//
// ═════════════════════════════════════════════════════════════════════════════════════════════
// 🔴 RUN TG — PN's FOUR FLAGS, RE-MEASURED. **THREE OF THE FOUR ARE NOT DEAD.**
//
// PN flagged four reads as "things this mapping reads that the shipped v2 envelope does not send",
// left them deliberately, and was right about every one — **on v2**. Its own header says what it
// measured: `ops/run-pn/fixture-answer-v2.json`, one delivered v2 envelope. RUN TG was briefed to
// delete them and measured them first, on the path PN did not have in front of it.
//
// 🔴 **`turn.answer` IS NOT ALWAYS v2, AND THIS FUNCTION HAS NO KIND BRANCH.**
//   · `routes/clark-answer.js:283` and `:716` still call `closeResearchChat` with a **v1
//     contract** — the fall-through taken whenever the pipeline does not return
//     `kind === "clark.answer.v2"`. `clarkAnswerSnapshot.js:265` treats a missing top-level `kind`
//     AS the v1 path, by design, and writes a v1-shaped snapshot.
//   · `chatsApi.js:97` hydrates whatever came back — `{...m.answer, streaming:false,
//     restored:true}` — with no branch on shape.
//   · `index.jsx:180` is `turns.filter((t) => t.answer)`, and `:897` maps ALL of them through
//     here. The THREAD renderer branches on `answer.kind` (`index.jsx:302`). **The RAIL does not.**
//
// MEASURED, on a v1 snapshot produced by the shipped builder itself
// (`ops/run-tg/step3-mkfixture-v1.cjs`, before/after in `ops/run-tg/out/`):
//
//   fixture            shape  authorities  checkedThrough  treatment
//   answer-R1-paras     v2         5           null        [null × 5]
//   answer-RQ07-wall    v2         8           null        [undef, null × 7]
//   restored-v1         v1         3        "2026-03-31"   ["no-known-negative", "caution", undef]
//
// So, flag by flag:
//
//   1. ⚠️ `treatment: … pillFlag(a.goodLaw)` — **KEPT.** Dead on v2 (no `goodLaw` on the row;
//      `pillFlag(undefined)` returns null and the card renders no pill — silent, not lying). But
//      `snapAuthority` (clarkAnswerSnapshot.js:79-86) freezes `goodLaw` VERBATIM on every v1 row,
//      and the measurement above shows two real pills coming out of it. Deleting this read blanks
//      the treatment pill on every restored v1 turn.
//   2. ✅ `checkedThrough` — **FIRST ARM KEPT, SECOND ARM DELETED.** PN said both arms are absent;
//      both are absent **on v2**. On v1 the envelope carries `checkedThrough` and the first arm
//      returns it (measured: "2026-03-31"). The SECOND arm — the map over `a.goodLaw?.checkedThrough`
//      — is the one that is dead on BOTH paths, and provably so rather than by measurement: on v2
//      there is no `goodLaw` at all, and on v1 `clarkAnswerSnapshot.js:339-340` computes the
//      envelope's own `checkedThrough` with the **identical expression** over the **same rows**, so
//      `??` can never fall through to it with a non-null result. It is not a fallback; it is the
//      same value computed twice, downstream, from a row shape only one of the two envelopes has.
//   3. ⚠️ `restored: Boolean(ans.restored)` — **KEPT**, as briefed. `chatsApi.js:97` sets it.
//   4. ⚠️ `.concat(ans.statutes ?? [])` — **KEPT.** A no-op on v2, which sends no `statutes` key
//      and files statutes inside `authorities` with `kind: "statute"`. The v1 contract carries them
//      in their OWN array and `buildAnswerSnapshot` returns it (`authorities, statutes,` at :328).
//      This concat is the only thing that puts a v1 statute row in the rail — measured 3 rows, not
//      2. Deleting it drops § 1983 out of a restored answer that rested on it.
//
// ⭐ THE GENERAL SHAPE: a field is not dead because the newest envelope stopped sending it. It is
// dead when NO producer sends it. Two of these had a live producer that a v2-only fixture cannot
// see — and the brief's own gate for this edit was "byte-compare on RA's two fixtures", both of
// which are v2. A gate that cannot fail is not a proof; the third fixture exists for that reason.
//
// The comments carried down from index.jsx below are still MOVED text, not this run's prose, and
// are edited here only where PN recorded them as false of the shipped shape.
//
// ═════════════════════════════════════════════════════════════════════════════════════════════
// 🔴 RUN VC — TWO ADDITIONS, AND BOTH ARE ABOUT WHAT THE RAIL IS ALLOWED TO PRINT.
//
//   1. `citeSource` ON EVERY AUTHORITY ROW. RUN PA put it on the envelope's `stripped`/`unchecked`
//      rows and stated the display fix was a later wave's. This is that wave for the rail. A row
//      in `authorities` resolved against the corpus and carries NO `citeSource` at all, so the
//      read is `?? null` and null MEANS corpus-supplied — the honest default, and the only one
//      that does not require every producer to be updated before the rail can be trusted.
//
//   2. `seeAlso` — THE AUTHORITIES THE CORPUS COULD NOT CONFIRM. `stripped` ("we looked and it is
//      not there") and `unchecked` ("we could not look") are two different findings and the
//      envelope keeps them apart deliberately (clarkAnswerV2.js).
//
//      🔴🔴 RUN SH · SH-1 — VC FOLDED THEM INTO ONE RAIL GROUP AND THAT WAS WRONG. VC's argument
//      was *"the rail makes ONE claim about both — we could not confirm this — and each row keeps
//      its own `reason`, so the distinction is carried, not destroyed."* The `reason` IS carried.
//      But the rail does not print the reason; it prints ONE SENTENCE over the whole group, and
//      the sentence VL wrote for it is *"Named in this answer. Not found in our corpus, so we
//      cannot vouch for it."*
//
//      **That sentence is true of `stripped` and FALSE of `unchecked`.** `stripped` is a claim
//      about THE LAW — we looked, and the corpus does not hold it. Evidence a reader may act on.
//      `unchecked` is a claim about US — our lookup did not complete. An ABSENCE of evidence, and
//      it licenses nothing. Telling a lawyer a case was *not found in our corpus* when we never
//      finished looking is the exact defect RUN 9 shipped live, about *TransUnion LLC v. Ramirez*,
//      **which is in the corpus** — reported absent because a redundant confirmation query was
//      cancelled. RUN C and RUN S built the two-array separation to make that unsayable, and
//      Research has honoured it since (`SeeAlsoCards` takes `stripped` only; `UncheckedList` is a
//      separate container with its own voice). One envelope, two apps, and until this commit they
//      told a lawyer different things about the same authority.
//
//      ⭐ SO THE MAPPING EMITS TWO ARRAYS, `seeAlso` AND `unchecked`, THROUGH ONE ROW MAPPER. Two
//      arrays and not a `bucket` discriminator on one, because a discriminator makes the split a
//      thing the RENDERER has to remember to do, and the renderer forgetting is the whole defect.
//      A group that cannot be handed the wrong rows cannot say the wrong sentence about them.
//
//      ⚠️ AND THE `stripped` SENTENCE IS NOT SOFTENED. It is correct, and it is the claim that
//      licenses that group. The defect was the `.concat`, never the words.
//
//      🔴 EVERY ROW IN THIS GROUP IS `model_asserted`, AND THAT IS WHY THE CITE TRAVELS SEPARATELY
//      FROM THE NAME. `cite` here is a reporter citation THE MODEL PRODUCED — never read out of
//      the corpus. RUN PJ's finding stands: *the product displayed a model-generated reporter
//      citation inside a sentence saying it could not verify that citation.* The mapping therefore
//      hands the renderer the fact (`citeSource`) rather than a pre-formatted string, and
//      `SourcesTurn` is where the never-bare rule is enforced. A mapping that concatenated name
//      and cite here would put the decision in the wrong file and out of the renderer's reach.
// ═════════════════════════════════════════════════════════════════════════════════════════════

import { seeAlsoKind } from "@citeclerk/ui/courtRuleCite";
import { scopeStampLabel } from "../jurisdictions.js";
import { pillFlag } from "./AuthorityCard.jsx";

export default function railPayload(t) {
  const ans = t.answer || {};
  return {
    id: t.id,
    q: ans.q || "",
    streaming: Boolean(ans.streaming),
    restored: Boolean(ans.restored),
    // The as-of date the citator stamped WHEN THIS ANSWER WAS GIVEN. On a restored turn it comes
    // from the snapshot and is deliberately not refreshed — true of the v1 envelope, which carries
    // this field (clarkAnswerSnapshot.js:339). A v2 answer has no as-of date at all and this is
    // null on every one of them, which is why SourcesTurn guards the line with `checkedThrough &&`
    // rather than printing an undated "Treatment checked through".
    //
    // 🔴 RUN TG removed the second arm — `(ans.authorities ?? []).map((a) =>
    // a.goodLaw?.checkedThrough).filter(Boolean).sort()[0]`. See flag 2 in the header: it is the
    // same expression clarkAnswerSnapshot.js:339-340 already ran over the same rows to produce the
    // envelope field the first arm reads, so it could never return a value the first arm had not
    // already returned.
    checkedThrough: ans.checkedThrough ?? null,
    // The same per-turn scope stamp the thread renders — one value, two renderings.
    scopeLabel: scopeStampLabel(ans.scope ?? null),
    /* \u2550\u2550 \ud83d\udd34 RUN AH \u00b7 \u00a75 \u2014 THIS MAPPING SPOKE ONLY THE OLD AUTHORITY SHAPE. \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550
       Every field below was named for the RETRIEVAL-era authority (`caseId`, `type`,
       `role`). The v2 envelope \u2014 which is what both Research and Clark have produced since
       RUN 9, and what the handoff carries \u2014 names them `clusterId`, `kind` and `why`. So
       for a v2 answer this mapping read three fields that are never present, and the rail
       rendered accordingly. MEASURED on the driven round trip, all six authorities of
       "What is the standard for qualified immunity?":

         \u00b7 `caseId` was null on every row \u21d2 `researchHref` returned null \u21d2 every card said
           **"Not linked to an opinion record"**, which is a CLAIM \u2014 it means "we have no
           cluster for this" \u2014 about six authorities that had resolved to clusters. The
           card's own comment is right that a caption is not an identifier; here the
           identifier was present and was not read.
         \u00b7 `name` was never passed at all, and `role` fell through to `a.name`, so the case
           NAME rendered in the why-relied-on slot and the name slot was empty.
         \u00b7 `type` was undefined \u21d2 no Case/Statute badge on any row.

       \ud83d\udd34 AND IT IS NOT A HANDOFF BUG. The rail maps whatever is in `turn.answer`, so a
       question asked IN Clark reached exactly the same three defects. The handoff is only
       where it was noticed, because that is where two surfaces show the same answer side
       by side and the difference is visible.

       BOTH SHAPES, `??`-CHAINED, RATHER THAN A SHAPE SWITCH: the two are additive and a
       branch on `kind === 'clark.answer.v2'` here would be a second place that has to know
       which envelope it is looking at. */
    authorities: (ans.authorities ?? []).concat(ans.statutes ?? []).map((a) => ({
      id: a.id, caseId: a.caseId ?? a.clusterId ?? null,
      relied: Boolean(a.relied), reliedKnown: a.reliedKnown,
      name: a.name ?? null,
      kind: a.kind ?? null,
      type: a.type ?? (a.kind === "statute" ? "Statute" : a.kind === "case" ? "Case" : undefined),
      cite: a.cite || "\u2014",
      // 🔴 RUN TG — THE DECISION YEAR, AT 0 ms. It is on the v2 row (`snapV2Authority` freezes it
      // too), RUN SB put it there deliberately, and RB's modal header wants it without a fetch.
      // `?? null` rather than a bare read: a v1 row has no `year` (it carries `date`), and this
      // mapping serves both — a stable null is a field the header can test, an absent key is not.
      // 🔴 RUN UC — `court` IS NOW ON THE v2 ROW, so the read TG withheld is added here. TG's note
      // said reading `a.court` "would emit a permanent undefined and look like the field had been
      // wired", and that was true of the envelope as it stood; `clarkAnswerV2.js` emits it as of
      // RUN UC · STEP 3 and `snapV2Authority` freezes it, so the read now returns a real value on a
      // live turn AND on a restored one. `?? null` for the same reason as `year`: a v1 row has no
      // `court` either, and this mapping serves both shapes with no kind branch.
      year: a.year ?? null,
      court: a.court ?? null,
      /* 🔴 RUN VC — WHERE THIS CITATION CAME FROM. `toV2Envelope` writes `citeSource:
         'model_asserted'` on `stripped` and `unchecked` rows and writes NOTHING on the resolved
         `authorities` rows — because a resolved row's cite was read out of the corpus. So an
         ABSENT value is the corpus-supplied case and is mapped to null rather than defaulted to a
         string: inventing `'corpus'` here would put a claim on the row that no producer made.
         ⭐ The never-bare rule tests for the POSITIVE value (`=== 'model_asserted'`), never for
         the absence of one — so a future producer that starts sending a third value cannot fall
         through the guard into "print it bare". */
      citeSource: a.citeSource ?? null,
      /* `why` is the ANSWER'S OWN SENTENCE naming this authority (`whySource` says so).
         It is the only honest thing to put in this slot; `a.name` \u2014 what fell through here
         before \u2014 put the case's name under a heading that reads "why this was relied on".
         Null when the model listed it without writing it into the prose, which RUN 5
         measured as the common shape, and the card renders its own absent-state for that. */
      role: a.role || a.why || "",
      // Same fold as every other surface. Passing the raw goodLaw through would have been
      // the divergence STEP 4 exists to prevent, introduced by the step itself.
      // A statute has no treatment because it has no citing court; the kind test now
      // catches the v2 spelling as well, which it did not before.
      // ⚠️ RUN TG: `a.goodLaw` is undefined on every v2 row and `pillFlag` returns null for it —
      // no pill, which is the honest rendering of a check that never ran. It is NOT undefined on a
      // restored v1 row, where the snapshot froze the verdict; measured "no-known-negative" and
      // "caution". Kept for that path. See flag 1 in the header.
      treatment: (a.type === "Statute" || a.kind === "statute") ? undefined : pillFlag(a.goodLaw),
    })),
    /* ══ 🔴 RUN VC — `SEE ALSO`: NAMED IN THE ANSWER, NOT CONFIRMED BY THE CORPUS. ═══════════════
       🔴 RUN SH · SH-1 — TWO ARRAYS, NOT A `.concat`. See flag 2 in the header for why; the short
       version is that one group can only carry one sentence, and the sentence is false of half the
       rows a concatenation puts under it. `seeAlso` is `stripped` and ONLY `stripped`.

       Neither is merged into `authorities`: a row in that list is there because it RESOLVED, and
       the resolution status the rail's other groups rest on would stop meaning anything.

       ⚠️ NO `id` IS EMITTED BY EITHER ARRAY. The envelope gives these rows `kind`, `name`, `cite`,
       `citeSource`, `reason` and `inProse` and nothing else — read from clarkAnswerV2.js:408 and
       :447, not from a comment. A React key is synthesised from the position and the two identifying
       fields; it is a key, not an identifier, and nothing else may use it.

       `type` is the same fold the resolved rows get, so the eyebrow above the card is computed in
       ONE place for all three groups. */
    /* ══ 🔴🔴 RUN CR · BLOCK 6 — ONE GROUP AGAIN, AND THIS TIME THE SENTENCE IS GONE FIRST. ══
       Adam: *"the cases listed should just be put into their own see also cards too"*, together
       with the removal of the note above the group and the closing note below it. Every point
       applies to Clark and Research alike, so `apps/steel/.../SeeAlsoCards.jsx` takes the identical
       change in the same commit.

       🔴 THIS IS NOT RUN VC's `.concat` COMING BACK, AND THE DIFFERENCE IS THE WHOLE POINT.
       SH-1 reverted that fold and its argument was exact: *"the rail does not print the reason; it
       prints ONE SENTENCE over the whole group,"* and VL's sentence — *"Not found in our corpus, so
       we cannot vouch for it"* — is TRUE of `stripped` and FALSE of `unchecked`. Telling a lawyer
       *TransUnion LLC v. Ramirez* was absent when the lookup had merely been cancelled is the
       defect RUN 9 shipped live, and it is the reason that sentence may never sit over these rows.
       ⭐ THE SENTENCE IS DELETED IN THIS SAME COMMIT. With no sentence over the group there is no
       false claim for an `unchecked` row to fall under: a group that asserts nothing about its rows
       cannot assert the wrong thing about half of them. SH-1's hazard was the sentence, not the
       adjacency.
       ✅ AND THE ROW-LEVEL DISTINCTION IS UNTOUCHED. `seeAlsoRows` is still one mapper over two
       source arrays, every row still carries its own `reason` and `citeSource`, the key prefixes
       still differ (`sa` vs `uc`) so a row that is stripped on one turn and unchecked on the next
       cannot collide, and `group` is carried onto the row so the DOM keeps the two tellable apart.
       Only the printed sentences are gone. */
    seeAlso: [
      ...seeAlsoRows(ans.stripped, "sa").map((r) => ({ ...r, group: "stripped" })),
      ...seeAlsoRows(ans.unchecked, "uc").map((r) => ({ ...r, group: "unchecked" })),
    ],
    /* ══ 🔴🔴 RUN SH · SH-1 — `unchecked`: WE COULD NOT LOOK. A DIFFERENT KIND OF SENTENCE. ══════
       Same rows, same mapper, same never-bare handling — and a SEPARATE ARRAY, so the renderer
       cannot put them under the "not found in our corpus" note by forgetting something. The key
       prefix differs (`uc` vs `sa`) because these are two lists and a React key collision between
       a stripped row and an unchecked row naming the same authority is a real shape: a row can be
       stripped on one turn and unchecked on the next. */
    /* RUN CR · BLOCK 6 — still emitted, and now EMPTY BY CONSTRUCTION on this mapping: the rows
       are in `seeAlso` above. The key stays so `AuthorityCard`'s empty-state test
       (`authorities.length === 0 && seeAlso.length === 0 && unchecked.length === 0`) and every
       probe that reads the payload keep their shape rather than reading `undefined.length`. */
    unchecked: [],
  };
}

/* ⭐ ONE MAPPER, TWO CALLERS — SO THE TWO GROUPS CANNOT DRIFT INTO DESCRIBING THEIR ROWS
   DIFFERENTLY. The only thing that differs between them is the SENTENCE the renderer puts above
   the cards, which is the one thing that genuinely IS different about them. */
/* 🔴 RUN RQ · §2 — THE PILL VOCABULARY, FROM `apps/main/source-types.js`, THE CANONICAL REGISTRY.
   `Case` and `Statute` are RUN CS's Magic Cite chip names for `resolve_citation` and
   `resolve_statute`; `Regulation` is `resolve_cfr`'s, and its `--cc-type-regulation*` tokens have
   shipped in all 19 themes since the theme generator was written — this adds no hex, so
   `rawcolor-gate`'s down-only floor is untouched. NO EIGHTH TYPE IS INVENTED: a rule of procedure
   is `resolve_statute`'s own `clarkWhen` ("a federal/state code section, court rule, …"), which is
   why it arrives here as `statute` rather than as a fourth word. */
const SEE_ALSO_PILL = { statute: "Statute", regulation: "Regulation", case: "Case" };

function seeAlsoRows(rows, keyPrefix) {
  return (rows ?? []).map((s, i) => ({
    key: `${keyPrefix}${i}:${s.name ?? ""}:${s.cite ?? ""}`,
    kind: seeAlsoKind(s),
    /* 🔴 RUN RQ · §2 — THE THIRD VALUE, AND THE REASON THE MAP IS A TABLE NOW. This was
       `s.kind === "statute" ? "Statute" : s.kind === "case" ? "Case" : undefined` — two values and
       an `undefined`, and `answer.css`'s chip rule defaults an unknown `data-kind` to the CASE
       tokens by its own comment, so `undefined` did not mean "no chip", it meant "a Case chip".
       Three ways to say Case and none to say anything else. The envelope now sends the shipped
       source type the registry assigns (apps/steel/server/lib/seeAlsoKind.js), so this reads it. */
    /* 🔴 RUN RQ — ASKED OF THE CITE ON THE READ PATH, NOT JUST STAMPED ON THE WIRE. A FROZEN or
       STORED envelope carries the `kind` the server wrote when it was CAPTURED, so the server-side
       stamp alone leaves every already-captured row wearing the old pill. MEASURED on the homepage
       chip's own frozen `Fed. R. Civ. P. 56`: still a **Case** pill after the server fix. Running
       the same shared rule here corrects them on render, which is the same read-path remedy
       `dropUnsupportedSentences` applies to legacy prose two files away. */
    type: SEE_ALSO_PILL[seeAlsoKind(s)] ?? undefined,
    name: s.name ?? null,
    cite: s.cite ?? null,
    // 🔴 DEFAULTS TO `model_asserted`, WHICH IS THE OPPOSITE DIRECTION FROM THE RESOLVED ROWS
    // ABOVE, AND DELIBERATELY SO. A row is in this group precisely BECAUSE the corpus did not
    // confirm it, so an absent `citeSource` here cannot mean "the corpus supplied this cite".
    // ⭐ Each default falls the way that keeps the rail from over-claiming, which is why they
    // differ: one absence means "verified", the other means "not verified", and a single shared
    // default would have to be wrong about one of them.
    citeSource: s.citeSource ?? "model_asserted",
    reason: s.reason ?? null,
    inProse: Boolean(s.inProse),
  }));
}
